// ── arcadeTable — живой стол темы «Аркада» ───────────────────────────────────
// Под экраном закручивается спиральный вихрь красного и синего на тёмной основе — как
// фон Balatro. Рисует видеокарта (WebGL) в холст втрое меньше экрана, браузер плавно
// растягивает его — краска мягкая, без пикселей, а работы немного (30 кадров в секунду). Свёрнуто
// приложение или вкладка — не рисует. «Меньше анимации» в системе — один неподвижный кадр.
//
// У каждого раздела свои цвета стола (THEMES.arcade.scenes) — как в Balatro стол меняет
// цвет от блайнда к блайнду: setTableScene(colors) плавно, за секунду, перетекает в них.
//
// Отклик: событие window 'ac:juice' { tone: 'good'|'again'|'hard'|'easy', combo } — стол
// на миг вспыхивает цветом ответа, а на серии верных ответов вихрь разгоняется (чем
// длиннее серия, тем быстрее; затихает сам за несколько секунд). Шлёт повторение.
//
// Нет WebGL (старый телефон, потерян контекст) — холста нет, вместо него неподвижный узор
// из index.css (html[data-theme="arcade"]:not([data-table-live]) body::before).
//
//   startArcadeTable(THEMES.arcade.table)  — App, когда тема с table
//   setTableScene(THEMES.arcade.scenes.review) — App, при смене раздела
//   stopArcadeTable()                      — другая тема

// Холст меньше экрана, браузер плавно растягивает его: краска мягкая, без
// видимых пикселей. Краска с резкими переходами при холсте втрое меньше давала «зерно» из
// квадратиков — поэтому вдвое (телефон — в 2.5 раза, и на одну октаву шума меньше, OCT).
const COARSE = typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
const pixSize = () => (COARSE ? 2.5 : 2);
const FPS = 30;
const PULSE = { good: '#4bc292', easy: '#009dff', hard: '#fda200', again: '#fe5f55' };

const VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}';
// Стол — «жидкая краска», как фон Balatro (без спирали): поле шума несколько раз
// «размешивается» само собой (domain warping: точка сдвигается шумом, сдвинутая — ещё раз),
// получаются перетекающие разводы. Тёмная основа, сочный цвет раздела, на гребнях — второй
// цвет; переходы резкие — краска жирная и контрастная; редкие тусклые искорки.
// Точность — высокая, где видеокарта умеет: на телефоне обычная (mediump) — 16 бит, и шум
// краски распадался на квадратики («рябит»). Случайное — без синуса (hash Dave Hoskins):
// он устойчив и при низкой точности.
const FRAG = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 uRes;uniform float uTime;uniform vec3 uInk;uniform vec3 uRed;uniform vec3 uBlue;
uniform float uLight;uniform vec3 uPulse;uniform float uPulseAmt;uniform vec4 uRip;
float hash(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}
float noise(vec2 p){
  vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
  return mix(mix(hash(i),hash(i+vec2(1.,0.)),f.x),mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),f.x),f.y);
}
float fbm(vec2 p){
  float v=0.,a=.5;
  mat2 m=mat2(1.6,1.2,-1.2,1.6);
  for(int i=0;i<OCT;i++){v+=a*noise(p);p=m*p;a*=.5;}
  return v;
}
void main(){
  // Волна от нажатия (uRip: x, y в точках холста, возраст в секундах, сила): кольцо
  // расходится от пальца и сдвигает краску — стол «отвечает» на касание.
  vec2 fc=gl_FragCoord.xy;
  float ring=0.;
  if(uRip.w>0.){
    vec2 d=fc-uRip.xy;
    float dist=length(d);
    ring=exp(-pow((dist-uRip.z*80.)/7.,2.))*(1.-uRip.z/1.2)*uRip.w;
    fc+=d/(dist+.001)*ring*5.;
  }
  // крупные разводы: мельче точки холста краска ломалась лесенкой («рябит»)
  vec2 uv=fc/uRes.y*1.5;
  float t=uTime*.06;
  vec2 q=vec2(fbm(uv+vec2(0.,t)),fbm(uv+vec2(5.2,1.3)-t));
  vec2 r=vec2(fbm(uv+2.6*q+vec2(1.7,9.2)+t*.7),fbm(uv+2.6*q+vec2(8.3,2.8)-t*.6));
  float f=fbm(uv+2.6*r);
  vec3 col=mix(uInk,uRed,smoothstep(.33,.58,f));
  col=mix(col,uBlue,smoothstep(.58,.8,f)*clamp(length(r)*1.1,0.,1.));
  col=mix(col,uInk*.55,smoothstep(.32,.05,f)*.7);
  // мягкий свет на гребнях краски — широкий; мелкий блик по наклону рябил «волосками»
  col+=uBlue*.18*smoothstep(.62,.9,f);
  // искорки: очень редкие квадратики, медленно всплывают; яркость ровная и тусклая —
  // мерцание и густота рябили в глазах (17.10 пользователь)
  vec2 sq=fc/(uRes.y*.11);
  sq.y-=uTime*.06;
  vec2 id=floor(sq);
  float h=hash(id);
  if(h>.985){
    vec2 o=(vec2(hash(id+3.1),hash(id+7.7))-.5)*.6;
    vec2 fr=fract(sq)-.5-o;
    float s=step(max(abs(fr.x),abs(fr.y)),.04+.03*hash(id+1.3));
    col+=vec3(1.,.93,.8)*s*.16;
  }
  // Виньетка по краям и мягкий свет сверху — прямо в рисунке.
  vec2 sc=gl_FragCoord.xy/uRes;
  float vig=smoothstep(1.,.25,length((sc-.5)*vec2(1.,1.15)));
  col*=mix(.62,1.,vig);
  col+=vec3(1.,.96,.88)*.06*smoothstep(.6,1.,sc.y);
  col*=uLight;
  col+=ring*.10;
  col=mix(col,uPulse,uPulseAmt);
  gl_FragColor=vec4(col,1.);
}`;

const rgb = (hex) => {
  const n = parseInt(String(hex).replace('#', ''), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

let st = null; // { canvas, gl, u, colors, goal, cur, light, time, boost, prev, raf, last, pulse, still, off }

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) || 'shader');
  return sh;
}

function setup(canvas) {
  const gl = canvas.getContext('webgl', { antialias: false, depth: false, alpha: false, powerPreference: 'low-power' });
  if (!gl) return null;
  const prog = gl.createProgram();
  gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
  const head = `#define OCT ${COARSE ? 3 : 4}\n`; // октав шума: на телефоне на одну меньше
  gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, head + FRAG));
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog) || 'link');
  gl.useProgram(prog);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW); // один треугольник на весь экран
  const loc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  const u = {};
  for (const k of ['uRes', 'uTime', 'uInk', 'uRed', 'uBlue', 'uLight', 'uPulse', 'uPulseAmt', 'uRip']) u[k] = gl.getUniformLocation(prog, k);
  return { gl, prog, u };
}

function resize() {
  if (!st) return;
  const px = pixSize();
  const w = Math.max(1, Math.ceil(window.innerWidth / px));
  const h = Math.max(1, Math.ceil(window.innerHeight / px));
  if (st.canvas.width !== w || st.canvas.height !== h) { st.canvas.width = w; st.canvas.height = h; }
  st.gl.viewport(0, 0, w, h);
  if (st.still || st.held) draw(performance.now());
}

// Текущие цвета стола плавно догоняют цвета сцены (за ~1 с), скорость вихря — свою цель.
function step(now) {
  const dt = Math.min(0.1, Math.max(0, (now - (st.prev || now)) / 1000));
  st.prev = now;
  const k = st.still ? 1 : Math.min(1, dt * 3);
  for (const key of ['ink', 'red', 'blue']) {
    const cur = st.cur[key];
    const to = st.goal[key];
    for (let i = 0; i < 3; i++) cur[i] += (to[i] - cur[i]) * k;
  }
  st.light += ((st.colors.light ?? 1) - st.light) * k;
  st.speed += ((st.colors.speed ?? 1) - st.speed) * k;
  // разгон серии затухает: через ~6 с без ответов — обычная скорость сцены (speed)
  st.boost *= Math.exp(-dt / 2.5);
  st.time += dt * st.speed * (1 + st.boost);
}

function draw(now) {
  const { gl, u } = st;
  step(now);
  const t = st.still ? 12 : st.time;
  let amt = 0;
  if (st.pulse && !st.still) {
    const pdt = (now - st.pulse.at) / 1000;
    amt = pdt < 1.2 ? 0.24 * Math.exp(-pdt * 4) : 0;
    if (!amt) st.pulse = null;
  }
  gl.uniform2f(u.uRes, st.canvas.width, st.canvas.height);
  gl.uniform1f(u.uTime, t);
  gl.uniform3fv(u.uInk, st.cur.ink);
  gl.uniform3fv(u.uRed, st.cur.red);
  gl.uniform3fv(u.uBlue, st.cur.blue);
  gl.uniform1f(u.uLight, st.light);
  gl.uniform3fv(u.uPulse, st.pulse?.color ? rgb(st.pulse.color) : st.cur.ink);
  gl.uniform1f(u.uPulseAmt, amt);
  const rip = st.rip && !st.still ? (now - st.rip.at) / 1000 : 9;
  if (rip >= 1.2) st.rip = null;
  gl.uniform4f(u.uRip, st.rip ? st.rip.x : 0, st.rip ? st.rip.y : 0, rip, st.rip ? 1 : 0);
  gl.drawArrays(gl.TRIANGLES, 0, 3);
}

const goalOf = (c) => ({ ink: rgb(c.ink), red: rgb(c.red), blue: rgb(c.blue) });

// Цвета раздела: стол плавно перетекает в них (без анимации — сразу).
export function setTableScene(colors) {
  if (!st || !colors) return;
  st.colors = colors;
  st.goal = goalOf(colors);
  if (st.still) draw(performance.now());
}

function loop(now) {
  if (!st) return;
  st.raf = requestAnimationFrame(loop);
  if (now - st.last < 1000 / FPS - 2) return;
  st.last = now;
  draw(now);
}

function play() {
  if (!st || st.raf || st.still || st.held || document.hidden) return;
  st.raf = requestAnimationFrame(loop);
}
function pause() {
  if (st?.raf) { cancelAnimationFrame(st.raf); st.raf = 0; }
}

export function startArcadeTable(colors) {
  if (typeof window === 'undefined' || !colors) return;
  if (st) { setTableScene(colors); return; }
  const canvas = document.createElement('canvas');
  canvas.id = 'ac-table';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText = 'position:fixed;left:0;top:0;width:100vw;height:100vh;z-index:-1;pointer-events:none;display:block;';
  let ctx = null;
  try { ctx = setup(canvas); } catch (e) { console.warn('[table]', e?.message || e); }
  if (!ctx) return; // нет WebGL — остаётся неподвижный узор из index.css
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const goal = goalOf(colors);
  st = { canvas, ...ctx, colors, goal, cur: { ink: [...goal.ink], red: [...goal.red], blue: [...goal.blue] }, light: colors.light ?? 1, speed: colors.speed ?? 1,
    time: 20, boost: 0, prev: 0, raf: 0, last: 0, pulse: null, rip: null, still: !!reduce?.matches };
  const onVis = () => (document.hidden ? pause() : play());
  const onJuice = (e) => {
    if (!st || st.still) return;
    st.pulse = { color: PULSE[e.detail?.tone] || PULSE.good, at: performance.now() };
    const combo = e.detail?.combo || 0;
    if (e.detail?.tone === 'again') st.boost = 0;
    else st.boost = Math.max(st.boost, Math.min(2.4, combo * 0.25));
  };
  // Нажатие где угодно (arcadeJuice шлёт 'ac:ripple' с точкой экрана) — волна по столу.
  const onRipple = (e) => {
    if (!st || st.still) return;
    const px = pixSize();
    st.rip = { x: (e.detail?.x || 0) / px, y: st.canvas.height - (e.detail?.y || 0) / px, at: performance.now() };
  };
  const onMotion = () => { if (!st) return; st.still = !!reduce?.matches; if (st.still) { pause(); draw(performance.now()); } else play(); };
  const onLost = (e) => { e.preventDefault(); stopArcadeTable(); };
  // Экран с собственной анимацией (игра «Рой слов») просит стол замереть: 'ac:table-hold'
  // { on } — стол стоит на последнем кадре, пока не отпустят.
  const onHold = (e) => { if (!st) return; st.held = !!e.detail?.on; if (st.held) pause(); else play(); };
  window.addEventListener('resize', resize);
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('ac:juice', onJuice);
  window.addEventListener('ac:ripple', onRipple);
  window.addEventListener('ac:table-hold', onHold);
  reduce?.addEventListener?.('change', onMotion);
  canvas.addEventListener('webglcontextlost', onLost);
  st.off = () => {
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('ac:juice', onJuice);
    window.removeEventListener('ac:ripple', onRipple);
    window.removeEventListener('ac:table-hold', onHold);
    reduce?.removeEventListener?.('change', onMotion);
    canvas.removeEventListener('webglcontextlost', onLost);
  };
  document.body.prepend(canvas);
  document.documentElement.setAttribute('data-table-live', '');
  resize();
  draw(performance.now());
  play();
}

export function stopArcadeTable() {
  if (!st) return;
  pause();
  st.off?.();
  try { st.gl.getExtension('WEBGL_lose_context')?.loseContext(); } catch { /* уже потерян */ }
  st.canvas.remove();
  document.documentElement.removeAttribute('data-table-live');
  st = null;
}

// Отклик на действие: стол вспыхивает цветом ответа и разгоняется на серии (повторение
// зовёт после оценки; combo — сколько верных подряд).
export function juice(tone, combo = 0) {
  try { window.dispatchEvent(new CustomEvent('ac:juice', { detail: { tone, combo } })); } catch { /* старый браузер */ }
}
